import { Tooltip } from "antd";
import dayjs from "dayjs";
import AdvancedFormat from "dayjs/plugin/advancedFormat";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { v4 as uuidv4 } from "uuid";

import {
  IPromsDomainTotalGraphData,
  ISurveyFormDisplayRuleDomainLegend,
} from "../../../../../../@types/Proms";
import { CustomModalChartTick } from "../../../../../../components/ModalChart/CustomModalChartTick/CustomModalChartTick";
import { getGraphSeparators } from "../../../../../../utils/chartUtils";
import { roundToX } from "../../../../../../utils/unitOperations";
import { DateFilterOptionType } from "../../../MeasurementModal/components/CustomOptionsBar";
import { CustomPromsDomainTotalGraphDot } from "./components/CustomPromsDomainTotalGraphDot/CustomPromsDomainTotalGraphDot";
import { CustomPromsDomainTotalGraphLegend } from "./components/CustomPromsDomainTotalGraphLegend/CustomPromsDomainTotalGraphLegend";

type PromsLineGraphsProps = {
  data: IPromsDomainTotalGraphData[];
  minY: number;
  maxY: number;
  dateInterval: DateFilterOptionType;
  mainValues: any;
  legends: ISurveyFormDisplayRuleDomainLegend[];
  showValues: boolean;
  selectedId?: string;
  onDotClick: (surveyId: number) => void;
};

dayjs.extend(AdvancedFormat);

export function PromsDomainTotalGraph({
  data,
  minY,
  maxY,
  dateInterval,
  mainValues,
  legends,
  selectedId,
  showValues,
  onDotClick,
}: PromsLineGraphsProps) {
  const { t } = useTranslation();

  const {
    graphSeparators,
    dates,
    separatorCounter,
    tickFormat,
    viewType,
    isTickMultipleLine,
  } = getGraphSeparators(dateInterval, mainValues.minDate, mainValues.maxDate);

  const [selectedSurveyId, setSelectedSurveyId] = useState(selectedId);
  const [visibleTooltipId, setVisibleTooltipId] = useState(null);
  const [visibleTooltipDomain, setVisibleTooltipDomain] = useState(null);

  const chartRef = useRef<HTMLDivElement>(null);

  let referenceTicks: number[] = [];

  const refs: number[] = [
    ...graphSeparators,
    ...dates.filter((_, index) => index % separatorCounter === 0),
  ];

  referenceTicks = [...refs];

  const formattedData = data.map((item) => ({
    ...item,
    time: dayjs(item.time).valueOf(),
  }));

  const allowedDomains = new Set(legends.map((legend) => legend.domain));

  const domainColorMap = legends.reduce((acc, curr) => {
    acc[curr.domain] = curr.color;
    return acc;
  }, {} as Record<string, string>);

  const groupedData = formattedData.reduce((acc, item) => {
    if (!allowedDomains.has(item.domain)) return acc;

    if (!acc[item.domain]) {
      acc[item.domain] = {
        color: domainColorMap[item.domain] || "#000000",
        promsData: [],
      };
    }
    acc[item.domain].promsData.push(item);
    return acc;
  }, {} as Record<string, { color: string; promsData: typeof formattedData }>);

  const tickXFormatter = (value: string) => {
    if (!data || !data.length) return "";

    if (
      graphSeparators.includes(Number(value)) ||
      referenceTicks.includes(Number(value))
    ) {
      if (tickFormat === "Q") {
        return (
          value &&
          `Q${dayjs(value).format(tickFormat)}, ${dayjs(value).format("YY")}`
        );
      }

      return value && dayjs(value).format(tickFormat);
    }

    return "";
  };

  const handleXDomain = () => {
    if (!dateInterval || !data) return [0, 1];

    const { minDate, maxDate } = mainValues;
    let { startDate, endDate } = dateInterval;

    if (minDate && maxDate && endDate.diff(startDate, "year") > 5) {
      startDate = minDate;
      endDate = maxDate;
    }

    if (endDate.diff(startDate, "months") > 11) {
      endDate = endDate.add(1, "month");
    }

    return [startDate.valueOf(), endDate.valueOf()];
  };

  return (
    <ResponsiveContainer
      ref={chartRef}
      width="100%"
      height="90%"
      maxHeight={498}
      style={{ paddingTop: "20px" }}
    >
      <LineChart margin={{ right: 20, top: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />

        <YAxis
          tickMargin={10}
          dataKey="score"
          stroke="#D8D8D8"
          tickCount={11}
          axisLine={false}
          allowDecimals={false}
          width={48}
          domain={[minY, maxY]}
          tick={{
            fill: "#727272",
            fontSize: "12px",
            fontWeight: 400,
          }}
        />

        <XAxis
          tickMargin={14}
          dataKey="time"
          stroke="#D8D8D8"
          type="number"
          domain={handleXDomain()}
          tickFormatter={tickXFormatter}
          ticks={viewType === "yearly" ? graphSeparators : dates}
          tickLine={false}
          allowDecimals={false}
          height={48}
          minTickGap={100}
          interval={0}
          tick={
            isTickMultipleLine ? (
              <CustomModalChartTick />
            ) : (
              {
                fill: "#727272",
                fontSize: "12px",
                fontWeight: "400",
              }
            )
          }
          angle={refs.length >= 15 ? 30 : 0}
        />

        <Legend
          content={
            <CustomPromsDomainTotalGraphLegend
              mainValues={mainValues}
              empty={formattedData.length === 0 || !showValues}
              legends={legends}
              showValues={showValues}
            />
          }
        />

        {refs.map((ref) => (
          <ReferenceLine
            key={ref.toString()}
            x={ref}
            stroke="#D8D8D8"
            strokeDasharray="8 1000"
          />
        ))}

        {Object.entries(groupedData).map(([domain, { color, promsData }]) => {
          const handleTooltipTitle = (payload: any, isArchived?: boolean) => {
            const promScores = data.filter(
              (d) => dayjs(d.time).valueOf() === payload.time
            );

            const sortedPromScores = [...promScores].sort((a, b) => {
              const indexA = legends.findIndex((l) => l.domain === a.domain);
              const indexB = legends.findIndex((l) => l.domain === b.domain);
              return indexA - indexB;
            });

            return (
              <div
                key={uuidv4()}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                {sortedPromScores.map((score) => {
                  const legend = legends.find((l) => l.domain === score.domain);

                  if (legend) {
                    return (
                      <p>
                        <b style={{ fontWeight: 600 }}>{legend.name}: </b>
                        {roundToX(score.score, 2)}
                      </p>
                    );
                  }

                  return null;
                })}
                {isArchived && (
                  <p
                    style={{
                      opacity: 0.9,
                      fontSize: "12px",
                      color: "var(--colors-gray-60)",
                    }}
                  >
                    {t("patientPage_proms_archivedMarker")}
                  </p>
                )}
              </div>
            );
          };

          return (
            <Line
              key={`${domain}-${uuidv4()}`}
              data={promsData}
              type="monotone"
              dataKey="score"
              stroke={color}
              strokeWidth={2}
              strokeOpacity={0.38}
              isAnimationActive={false}
              dot={({ cx, cy, payload }: any) => {
                const isArchived = !!payload.archived;
                return (
                  <Tooltip
                    title={handleTooltipTitle(payload, isArchived)}
                    arrow={false}
                    placement="top"
                    zIndex={3000}
                    styles={{
                      body: {
                        padding: "10px 20px",
                        minHeight: "40px",
                        width: "max-content",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "'Open Sans', sans-serif",
                        fontSize: "12px !important",
                        fontWeight: "500",
                        backgroundColor: "#4b4b4b",
                      },
                    }}
                    mouseEnterDelay={0.1}
                    open={
                      visibleTooltipId === payload.id &&
                      visibleTooltipDomain === payload.domain
                    }
                  >
                    <svg width="100%" height="100%">
                      <CustomPromsDomainTotalGraphDot
                        cx={cx}
                        cy={cy}
                        payload={payload}
                        color={isArchived ? "var(--colors-gray-80)" : color}
                        key={uuidv4()}
                        selectedId={selectedSurveyId}
                        onDotClick={() => {
                          setVisibleTooltipId(null);
                          setSelectedSurveyId(payload.id);
                          onDotClick?.(payload.id);
                        }}
                        onMouseOver={() => {
                          setVisibleTooltipId(payload.id);
                          setVisibleTooltipDomain(payload.domain);
                        }}
                        onMouseOut={() => {
                          setVisibleTooltipId(null);
                          setVisibleTooltipDomain(null);
                        }}
                      />
                    </svg>
                  </Tooltip>
                );
              }}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}
